"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import "swiper/css";
import "swiper/css/navigation";
import { useHomeSliders } from "../hooks/useHomeSliders";
import { getRegionLocaleHref, getClientRegion } from "@/services/region";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { isTeacherAccount } from "@/modules/auth/utils/userAccess";

export default function HomeSlider() {
  const locale = useLocale();
  const t = useTranslations("web.home");
  const pathname = usePathname();
  const regionSlug = getClientRegion(pathname) || undefined;
  const { slides, loading } = useHomeSliders(locale, regionSlug);
  // Teacher accounts can't book, so the free-trial CTA is suppressed for them.
  const { user } = useCurrentUser();
  const hideBookingCta = isTeacherAccount(user);

  if (loading || slides.length === 0) {
    return null;
  }

  return (
    <div id="landingCarousel" className="relative top-0 w-full overflow-hidden">
      <Swiper
        modules={[Navigation, Autoplay]}
        navigation
        autoHeight={true}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={slides.length > 1}
        className="relative w-full"
      >
        {slides.map((slide, index) => {
          // The slider CTA is always labelled "book_free_trial", so it always
          // navigates to the free-trial booking page rather than each slide's
          // arbitrary `link`. The per-slide `link` is kept as the opt-in flag
          // for whether this slide shows the CTA at all.
          const href =
            slide.link && !hideBookingCta
              ? getRegionLocaleHref(pathname, locale, "/book/free-trial")
              : null;

          return (
            <SwiperSlide key={slide.id}>
              {/* Full width across all viewports (mobile, tablet, desktop)
                  so banner artwork spans 100% width cleanly without letterboxing or side margins. */}
              <div className="relative w-full bg-[#F7FBFE]">
                <picture className="block w-full">
                  {href ? (
                    <div className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 md:hidden">
                      <Link
                        href={href}
                        className="inline-block cursor-pointer whitespace-nowrap rounded-md bg-primary px-5 py-2 text-sm font-bold text-white shadow-md transition hover:bg-secondary"
                      >
                        {t("book_free_trial")}
                      </Link>
                    </div>
                  ) : null}

                  {slide.mobile_image ? (
                    <source media="(max-width: 767px)" srcSet={slide.mobile_image} />
                  ) : null}

                  {slide.image ? (
                    <img
                      src={slide.image}
                      alt={slide.title || ""}
                      // The first slide is the page's LCP element, so it must
                      // not be lazy-loaded; the rest can wait.
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      className="block w-full h-auto object-center"
                    />
                  ) : null}
                </picture>

                {href ? (
                  <div className={DESKTOP_BUTTON_POSITION}>
                    <Link
                      href={href}
                      className={`${DESKTOP_BUTTON_CLASS} cursor-pointer`}
                    >
                      {t("book_free_trial")}
                    </Link>
                  </div>
                ) : null}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}

/**
 * The desktop call to action used to be placed by two hardcoded percentage
 * offsets alternating on slide index, and styled navy or yellow on the same
 * alternation. Both were guesses at where a given banner happened to have empty
 * space, and they broke outright once the slide got a fixed height: with
 * `object-contain` the image is letterboxed inside the container, so
 * container-relative percentages no longer track the artwork at all - the
 * button landed on top of a speech bubble.
 *
 * It is now anchored to the bottom centre of the slide with real padding, which
 * is predictable on every slide regardless of artwork, and styled one way so
 * the primary action looks the same each time round the carousel.
 */
const DESKTOP_BUTTON_POSITION =
  "absolute inset-x-0 bottom-6 z-20 hidden justify-center md:flex";

const DESKTOP_BUTTON_CLASS =
  "inline-block whitespace-nowrap rounded-full bg-primary px-8 py-3 text-base font-bold text-white shadow-lg transition-colors duration-200 hover:bg-secondary lg:text-lg";
